/* eslint-disable new-cap */
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import jsPDFInvoiceTemplate, { OutputType } from 'jspdf-invoice-template';

import { formatCurrency } from '@C/TableGrid';
import { InterFont } from '@C/Utils';
import { authService } from '@S';
import { RobotoRegular, RobotoBold } from '@C/Utils';
import { useGlobalStore } from '@stores/globalStore';

export const getBase64FromUrl = async (url, maxWidth = 200, quality = 0.7) => {
  const data = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      Accept: 'image/png',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  });
  const blob = await data.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      // Compress using canvas
      const img = new window.Image();
      img.onload = () => {
        // Calculate new dimensions
        const scale = Math.min(1, maxWidth / img.width);
        const width = img.width * scale;
        const height = img.height * scale;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // Use PNG to preserve color and transparency
        const compressedBase64 = canvas.toDataURL('image/png'); // PNG preserves color
        resolve(compressedBase64);
      };
      img.src = base64data;
    };
  });
};

export async function generateInvoiceCC(data) {
  const bunkAddress = authService.currentBunkAddress;
  const slipData = data;
  const pdfTax = {
    value: 0,
    label: '',
  };
  if (slipData.tax.length > 0) {
    slipData.tax.map((item) => {
      pdfTax.value += item.percentage;
      pdfTax.label += `${item.name}/`;
    });
  }
  const pdfData = {
    outputType: OutputType.Save,
    returnJsPDFDocObject: true,
    fileName: `Invoice - ${slipData.credit_customer.name} - ${dayjs(
      slipData.time_stamp,
    ).format('DD-MM-YYYY')}`,
    orientationLandscape: false,
    compress: true,
    logo: {
      src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/Indian_Oil_Logo.svg/512px-Indian_Oil_Logo.svg.png',
      type: 'PNG', // optional, when src= data:uri (nodejs case)
      width: 25, // aspect ratio = width/height
      height: 30,
      margin: {
        top: 0, // negative or positive num, from the current position
        left: 0, // negative or positive num, from the current position
      },
    },
    stamp: {
      inAllPages: true, // by default = false, just in the last page
      src: 'https://res.cloudinary.com/hecto-networks/image/upload/v1659939986/Webp_net_resizeimage_b6fb9d01d3.png',
      type: 'JPG', // optional, when src= data:uri (nodejs case)
      width: 15, // aspect ratio = width/height
      height: 20,
      margin: {
        top: 0, // negative or positive num, from the current position
        left: 0, // negative or positive num, from the current position
      },
    },
    business: {
      name: authService.currentBunkName,
      address: `${bunkAddress.city}, ${bunkAddress.location}, ${bunkAddress.country.country} - ${bunkAddress.pincode}`,
    },
    contact: {
      label: 'Invoice issued for:',
      name: `${slipData.credit_customer.name.replace(/(^|\s)\S/g, function (t) {
        return t.toUpperCase();
      })}`,
    },
    invoice: {
      label: 'Invoice #: ',
      num: slipData.slip_no || 'NA',
      invGenDate: `Invoice Date: ${dayjs(slipData.time_stamp).format(
        'D MMMM YYYY',
      )}`,
      headerBorder: false,
      tableBodyBorder: false,
      header: [
        {
          title: '#',
          style: {
            width: 10,
          },
        },
        {
          title: 'Product',
          style: {
            width: 60,
          },
        },
        {
          title: 'Rate',
        },
        { title: 'Quantity' },
        { title: 'Amount (excl tax)' },
        { title: 'Amount (incl tax)' },
      ],
      table: [
        [
          1,
          `${slipData.product.name}`,
          `${slipData.product.price.toFixed(2)}`,
          `${slipData.quantity}`,
          `${slipData.after_discount.toFixed(2)}`,
          `${slipData.after_tax.toFixed(2)}`,
        ],
      ],
      additionalRows: [
        {
          col1: 'Total:',
          col2: `${slipData.after_discount.toFixed(2)}`,
          style: {
            fontSize: 10, // optional, default 12
          },
        },
        {
          col1: `Discount`,
          col2: `${slipData.discount || 0.0}`,
          style: {
            fontSize: 10, // optional, default 12
          },
        },
        {
          col1: `Tax: ${pdfTax.label || '--'}`,
          col2: ` ${pdfTax.value}%`,
          style: {
            fontSize: 10, // optional, default 12
          },
        },
        {
          col1: 'Grand Total:',
          col2: `${slipData.after_tax.toFixed(2)}`,
          style: {
            fontSize: 14, // optional, default 12
          },
        },
      ],
    },
    footer: {
      text: 'The invoice is created on a computer and is valid without the signature and stamp.',
    },
    pageEnable: true,
    pageLabel: 'Page ',
  };
  jsPDFInvoiceTemplate(pdfData);
}

function compressBase64ImagePreserveColor(base64Str, maxWidth = 100) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // In case it's a URL
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scaleFactor = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scaleFactor;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const compressedBase64 = canvas.toDataURL('image/png'); // ✅ PNG keeps color accuracy
      resolve(compressedBase64);
    };
    img.src = base64Str;
  });
}

export async function generateCompleteInvoiceCC(
  data,
  dataBalance,
  additionalData,
  resolve,
  reject,
  start_date,
  end_date,
  gst_no,
  enableOutstanding = false,
  enableDiscount = false,
  enableProductSummary = false,
  enableCustomerAddress = false,
  enableCustomerNotes = false,
) {
  const bunkLogo = getBase64FromUrl(authService.currentOMCWebLogo);
  const upiQr = additionalData.upi_qr;
  const creditCustomerId = data[0]?.credit_customer?.uuid || '';
  const { fetchCreditCustomer } = useGlobalStore.getState();
  const creditCustomer = await fetchCreditCustomer(creditCustomerId);

  // read promise value
  bunkLogo.then((result) => {
    // compress the image to reduce size
    compressBase64ImagePreserveColor(result, 100).then((compressedResult) => {
      result = compressedResult;
    });
    // calcualte image width and height based on aspect ratio
    const img = new Image();
    img.onload = function () {
      const doc = new jsPDF();
      doc.addFileToVFS('Roboto-Regular-normal.ttf', RobotoRegular);
      doc.addFileToVFS('Roboto-Bold-normal.ttf', RobotoBold);
      doc.addFont('Roboto-Bold-normal.ttf', 'Roboto-Bold', 'bold');
      doc.addFont('Roboto-Regular-normal.ttf', 'Roboto-Regular', 'normal');
      doc.setFont('Roboto-Regular', 'normal'); // Default regular

      const pageWidth = doc.internal.pageSize.getWidth();
      const textWidth = pageWidth - 40;
      const slipData = data;
      const margin = 5;
      const balanceData = dataBalance;
      const bunkCity = authService.currentBunkAddress.city || '';
      const bunkLocation = authService.currentBunkAddress.location || '';
      const bunkPincode = authService.currentBunkAddress.pincode || '';
      const bunkCountry = authService.currentBunkAddress.country.country || '';
      const tableData = [];
      const total = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
      }).format(slipData.reduce((a, b) => a + (b['after_tax'] || 0), 0) || 0);
      const discount = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
      }).format(slipData.reduce((a, b) => a + (b['discount'] || 0), 0) || 0);

      slipData.map((item, i) => {
        tableData.push([
          dayjs(item.time_stamp).format('DD/MM/YYYY'),
          item.slip_no || '--',
          item.vehicle?.vehicle_no || '--',
          item.product?.name || 'Cash given',
          item.quantity.toFixed(2),
          item.product?.price,
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(item.after_discount.toFixed(2)),
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(Number(item.after_tax - item.after_discount).toFixed(2)),
          ...(enableDiscount
            ? [
                Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                }).format(item.discount.toFixed(2)),
              ]
            : []),
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(item.after_tax.toFixed(2)),
        ]);
      });

      const tableData3 = [];
      balanceData.map((item, i) => {
        tableData3.push([
          // `${item.opening_date}: ${item.opening_balance_on_date.toFixed(2)}`,
          `Outstanding as on ${dayjs(item.closing_date).format('MMMM D, YYYY')}: ${new Intl.NumberFormat(
            'en-IN',
            {
              style: 'currency',
              currency: 'INR',
            },
          ).format(item.closing_balance_on_date.toFixed(2))}`,
        ]);
      });

      // group slipData by product.name and sum the quantity and make it as an array of arrays
      const groupedData = slipData.reduce((result, item) => {
        const productName = item.product?.name || 'Cash given';
        const foundItem = result.find((group) => group[0] === productName);
        if (foundItem) {
          foundItem[1] += +item.quantity;
          foundItem[2] += +item.after_tax;
        } else {
          result.push([productName, item.quantity, item.after_tax]);
        }
        return result;
      }, []);

      // map thourgh grouped data
      const tableData2 = [];
      groupedData.map((item, i) => {
        tableData2.push([item[0], item[1].toFixed(2), formatCurrency(item[2])]);
      });
      const aspectRatio = img.width / img.height;
      const width = 25;
      const height = width / aspectRatio;
      try {
        // Logo + Title
        doc.addImage(result, 'PNG', margin, 10, 25, height);
        doc.setFontSize(14);

        // Billed To & Fuel Station Info (side-by-side)
        const addressParts = [
          [creditCustomer?.address?.city, creditCustomer?.address?.state]
            .filter(Boolean)
            .join(', '),
          creditCustomer?.address?.pincode,
        ];
        const formattedAddress = addressParts.filter(Boolean).join(' - ');
        const customerNotes =
          additionalData.customer_notes ||
          creditCustomer?.notes ||
          creditCustomer?.credit_customer?.notes ||
          '';
        const leftInfo = [
          `Billed To`,
          slipData[0].credit_customer.name,
          `GST No: ${gst_no || 'N/A'}`,
          ...(enableCustomerAddress && creditCustomer?.address?.full_address
            ? [creditCustomer?.address?.full_address]
            : []),
          ...(enableCustomerAddress && formattedAddress
            ? [formattedAddress]
            : []),
          ...(enableCustomerNotes && customerNotes.trim() !== ''
            ? [`Notes: ${customerNotes}`]
            : []),
        ];
        const rightInfo = [
          `Invoice Details`,
          `Invoice Number: ${additionalData.invoice_number || '--'}`,
          `Date: ${dayjs(additionalData.invoice_date).format('DD MMM YYYY')}`,
          `Duration: ${dayjs(start_date).format('D MMM YY')} to: ${dayjs(end_date).format('D MMM YY')}`,
          `Bunk Details`,
          `${authService.currentBunkName}`,
          `Address: ${bunkCity}`,
          `${bunkLocation}${bunkPincode ? ` - ${bunkPincode}` : ''}`,
          `${bunkCountry}`,
          `GST: ${authService.currentUserGst || 'N/A'}`,
        ];

        let infoY = 50;
        leftInfo.forEach((line, idx) => {
          doc.setFontSize(10);
          if (idx === 0) {
            doc.setFont('Roboto-Bold', 'bold');
          } else {
            doc.setFont('Roboto-Regular', 'normal');
          }
          doc.text(line, margin, infoY + idx * 6);
        });
        doc.setFont('Roboto-Regular', 'normal');

        rightInfo.forEach((line, idx) => {
          let Y1 = 15;
          doc.setFontSize(10);
          if (idx === 0 || idx === 4) {
            doc.setFont('Roboto-Bold', 'bold');
          } else {
            doc.setFont('Roboto-Regular', 'normal');
          }
          if (idx >= 4) {
            // add a 4px margin top
            Y1 = 17;
          }
          doc.text(line, pageWidth - margin, Y1 + idx * 6, { align: 'right' });
        });

        infoY += Math.max(leftInfo.length, rightInfo.length) * 1;

        // add a horizontal line after the info
        doc.setLineWidth(0.1);
        doc.setDrawColor(0, 0, 0);
        doc.line(margin, infoY + 20, pageWidth - margin, infoY + 20);

        infoY += 35; // Adjust Y position for the table

        // Full Invoice Table
        autoTable(doc, {
          startY: infoY,
          head: [
            [
              'Date',
              'Slip No.',
              'Vehicle',
              'Product',
              'Quantity',
              'Rate',
              'Amt (excl tax)',
              'Tax',
              ...(enableDiscount ? ['Discount'] : []),
              'Amt (incl tax)',
            ],
          ],
          body: tableData,
          theme: 'plain', // changed from 'striped'
          didDrawCell: function (data) {
            if (data.section === 'head') {
              const { cell } = data;
              const { x, y, width, height } = cell;

              // Draw bottom border line for header cells
              doc.setDrawColor(0); // Black border
              doc.setLineWidth(0.1); // Border thickness
              doc.line(x, y + height, x + width, y + height);
            }
          },
          headStyles: {
            fillColor: '#ffffff',
            textColor: [0, 0, 0],
            font: 'Roboto-Bold',
            fontStyle: 'bold',
            halign: 'left',
            valign: 'middle',
            fontSize: 9,
            overflow: 'visible',
            wordBreak: 'normal',
            cellWidth: 'auto',
            cellPadding: { top: 1, right: 1, bottom: 2, left: 1 },
          },
          styles: {
            font: 'Roboto-Regular',
            fontSize: 9,
            halign: 'left',
            valign: 'middle',
            textColor: '#000000',
            wordBreak: 'normal',
            cellPadding: { top: 3, right: 1, bottom: 1, left: 1 },
            cellWidth: 'auto',
          },
          margin: { left: margin, right: margin },
        });

        // Summary Table
        if (enableProductSummary) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 10,
            head: [['Product', 'Quantity', 'Amt (incl tax)']],
            body: tableData2,
            theme: 'plain',
            didDrawCell: function (data) {
              if (data.section === 'head') {
                const { cell } = data;
                const { x, y, width, height } = cell;

                // Draw bottom border line for header cells
                doc.setDrawColor(0); // Black border
                doc.setLineWidth(0.1); // Border thickness
                doc.line(x, y + height, x + width, y + height);
              }
            },
            headStyles: {
              fillColor: '#ffffff',
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              font: 'Roboto-Bold',
              halign: 'left',
              valign: 'middle',
              fontSize: 9,
              cellPadding: { top: 1, right: 1, bottom: 2, left: 1 },
            },
            styles: {
              font: 'Roboto-Regular',
              fontSize: 9,
              halign: 'left',
              valign: 'middle',
              cellPadding: { top: 3, right: 1, bottom: 1, left: 1 },
              textColor: '#000000',
            },
            margin: { left: margin, right: margin },
            tableWidth: pageWidth - 2 * margin,
          });
        }

        // Billing Totals
        autoTable(doc, {
          body: [
            ...(enableDiscount
              ? [
                  [
                    {
                      content: `Total Discount: ${discount}`,
                      styles: {
                        halign: 'right',
                        font: 'Roboto-Bold',
                        fontSize: 10,
                        fontStyle: 'bold',
                      },
                    },
                  ],
                ]
              : []),
            [
              {
                content: `Billed Amount: ${total}`,
                styles: {
                  halign: 'right',
                  font: 'Roboto-Bold',
                  fontSize: 10,
                  fontStyle: 'bold',
                },
              },
            ],
            ...(enableOutstanding && tableData3.length > 0
              ? [
                  [
                    {
                      content: tableData3[0],
                      styles: {
                        halign: 'right',
                        font: 'Roboto-Bold',
                        fontStyle: 'bold',
                        fontSize: 10,
                      },
                    },
                  ],
                ]
              : []),
          ],
          theme: 'plain',
          margin: { left: margin, right: margin },
        });

        // Footer Message
        const footerStartY = doc.autoTable.previous.finalY + 12;
        // doc.setFontSize(10);
        // doc.text('THANK YOU ! FOR FUELLING WITH US !', pageWidth / 2, footerStartY, { align: 'center' });
        // doc.setFontSize(8);
        // doc.text('FOR ANY QUERIES AND COMPLAINT VISIT OUR CUSTOMER CARE', pageWidth / 2, footerStartY + 6, { align: 'center' });
        // doc.text('SAVE FUEL, SECURE THE FUTURE!', pageWidth / 2, footerStartY + 12, { align: 'center' });
        // doc.text(`TIME: ${dayjs(additionalData.invoice_date).format('HH:mm')}`, pageWidth / 2, footerStartY + 18, { align: 'center' });

        // Notes + QR Code (Optional)
        const userNotes = additionalData.notes || '';
        const hasUserNotes = userNotes.trim() !== '';
        const mergedNotes = hasUserNotes ? userNotes : '';
        const hasNotes = mergedNotes.trim() !== '';
        const hasQR = additionalData.upi_id !== '';

        if (hasNotes || hasQR) {
          autoTable(doc, {
            body: [
              [
                {
                  content: hasNotes ? mergedNotes : '',
                  styles: {
                    fontSize: 9,
                    cellWidth: pageWidth / 2,
                    halign: 'left',
                  },
                },
                {
                  content: hasQR ? 'Scan the below QR code to pay via UPI' : '',
                  styles: {
                    fontSize: 10,
                    halign: 'right',
                  },
                },
              ],
            ],
            theme: 'plain',
            margin: { left: margin, right: margin },
            styles: {
              overflow: 'linebreak',
            },
          });

          if (hasQR) {
            doc.addImage(
              upiQr,
              'PNG',
              pageWidth - 45,
              doc.autoTable.previous.finalY + 5,
              20,
              20,
            );
          }
        }

        // Save PDF
        doc.save(
          `Invoice - ${slipData[0].credit_customer.name} - ${dayjs(start_date).format('DD-MM-YYYY')} - ${dayjs(end_date).format('DD-MM-YYYY')}`,
        );
        resolve(true);
      } catch (error) {
        console.error('Error generating invoice:', error);
        reject('There is no data to generate invoice');
      }
    };
    img.src = result;
  });
}

export async function generateCompleteInvoiceLubes(
  data,
  dataBalance,
  additionalData,
  resolve,
  reject,
  start_date,
  end_date,
  enableDiscount = false,
  enableProductSummary = false,
  enableCustomerAddress = false,
) {
  const bunkLogo = getBase64FromUrl(authService.currentOMCWebLogo);
  const upiQr = additionalData.upi_qr;
  const customer_name = additionalData.customer_name || '';
  const gst_no = additionalData.gst_no || '';
  const creditCustomerId = data[0]?.credit_customer?.uuid || '';
  const { fetchCreditCustomer } = useGlobalStore.getState();
  const creditCustomer = await fetchCreditCustomer(creditCustomerId);
  // read promise value
  bunkLogo.then((result) => {
    // compress the image to reduce size
    compressBase64ImagePreserveColor(result, 100).then((compressedResult) => {
      result = compressedResult;
    });
    // calcualte image width and height based on aspect ratio
    const img = new Image();
    img.onload = function () {
      const doc = new jsPDF();
      doc.addFileToVFS('Roboto-Regular-normal.ttf', RobotoRegular);
      doc.addFileToVFS('Roboto-Bold-normal.ttf', RobotoBold);
      doc.addFont('Roboto-Bold-normal.ttf', 'Roboto-Bold', 'bold');
      doc.addFont('Roboto-Regular-normal.ttf', 'Roboto-Regular', 'normal');
      doc.setFont('Roboto-Regular', 'normal'); // Default regular

      const pageWidth = doc.internal.pageSize.getWidth();
      const textWidth = pageWidth - 40;
      const slipData = data;
      const margin = 5;
      const balanceData = dataBalance;
      const bunkCity = authService.currentBunkAddress.city || '';
      const bunkLocation = authService.currentBunkAddress.location || '';
      const bunkPincode = authService.currentBunkAddress.pincode || '';
      const bunkCountry = authService.currentBunkAddress.country.country || '';
      const tableData = [];
      const total = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
      }).format(
        slipData.reduce(
          (a, b) => a + (b['payment_for']['after_tax'] || 0),
          0,
        ) || 0,
      );
      const discount = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
      }).format(
        slipData.reduce((a, b) => a + (b['payment_for']['discount'] || 0), 0) ||
          0,
      );

      slipData.map((item, i) => {
        tableData.push([
          dayjs(item.time_stamp).format('DD/MM/YYYY'),
          item.payment_for.bill_number || '--',
          item.payment_for?.name,
          item.payment_for.selling_price,
          item.payment_for.quantity.toFixed(2),
          ...(enableDiscount
            ? [
                Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                }).format(item.payment_for.discount.toFixed(2)),
              ]
            : []),
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(item.payment_for.after_discount.toFixed(2)),
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(item.payment_for.after_tax.toFixed(2)),
        ]);
      });

      const tableData3 = [];
      balanceData.map((item, i) => {
        tableData3.push([
          // `${item.opening_date}: ${item.opening_balance_on_date.toFixed(2)}`,
          `Outstanding as on ${dayjs(item.closing_date).format('MMMM D, YYYY')}: ${new Intl.NumberFormat(
            'en-IN',
            {
              style: 'currency',
              currency: 'INR',
            },
          ).format(item.closing_balance_on_date.toFixed(2))}`,
        ]);
      });

      // group slipData by product.name and sum the quantity and make it as an array of arrays
      const groupedData = slipData.reduce((result, item) => {
        const productName = item.payment_for?.name;
        const foundItem = result.find((group) => group[0] === productName);
        if (foundItem) {
          foundItem[1] += +item.payment_for.quantity;
          foundItem[2] += +item.payment_for.after_tax;
        } else {
          result.push([
            productName,
            item.payment_for.quantity,
            item.payment_for.after_tax,
          ]);
        }
        return result;
      }, []);

      // map thourgh grouped data
      const tableData2 = [];
      groupedData.map((item, i) => {
        tableData2.push([item[0], item[1].toFixed(2), formatCurrency(item[2])]);
      });
      const aspectRatio = img.width / img.height;
      const width = 25;
      const height = width / aspectRatio;
      try {
        // Logo + Title
        doc.addImage(result, 'PNG', margin, 10, 25, height);
        doc.setFontSize(14);

        // Billed To & Fuel Station Info (side-by-side)
        const addressParts = [
          [creditCustomer?.address?.city, creditCustomer?.address?.state]
            .filter(Boolean)
            .join(', '),
          creditCustomer?.address?.pincode,
        ];
        const formattedAddress = addressParts.filter(Boolean).join(' - ');
        const leftInfo = [
          `Billed To`,
          `${customer_name}`,
          `GST No: ${gst_no || 'N/A'}`,
          ...(enableCustomerAddress && creditCustomer?.address?.full_address
            ? [creditCustomer?.address?.full_address]
            : []),
          ...(enableCustomerAddress && formattedAddress
            ? [formattedAddress]
            : []),
        ];
        const rightInfo = [
          `Invoice Details`,
          `Invoice Number: ${additionalData.invoice_number || '--'}`,
          `Date: ${dayjs(additionalData.invoice_date).format('DD MMM YYYY')}`,
          `Bunk Details`,
          `${authService.currentBunkName}`,
          `Address: ${bunkCity}`,
          `${bunkLocation}${bunkPincode ? ` - ${bunkPincode}` : ''}`,
          `${bunkCountry}`,
          `GST: ${authService.currentUserGst || 'N/A'}`,
        ];

        let infoY = 50;
        leftInfo.forEach((line, idx) => {
          doc.setFontSize(10);
          if (idx === 0) {
            doc.setFont('Roboto-Bold', 'bold');
          } else {
            doc.setFont('Roboto-Regular', 'normal');
          }

          doc.text(line, margin, infoY + idx * 6);
        });
        doc.setFont('Roboto-Regular', 'normal');

        rightInfo.forEach((line, idx) => {
          let Y1 = 15;
          doc.setFontSize(10);
          if (idx === 0 || idx === 3) {
            doc.setFont('Roboto-Bold', 'bold');
          } else {
            doc.setFont('Roboto-Regular', 'normal');
          }
          if (idx >= 4) {
            // add a 4px margin top
            Y1 = 17;
          }
          doc.text(line, pageWidth - margin, Y1 + idx * 6, { align: 'right' });
        });

        infoY += Math.max(leftInfo.length, rightInfo.length) * 1;

        // add a horizontal line after the info
        doc.setLineWidth(0.1);
        doc.setDrawColor(0, 0, 0);
        doc.line(margin, infoY + 20, pageWidth - margin, infoY + 20);

        infoY += 35; // Adjust Y position for the table

        // Full Invoice Table
        autoTable(doc, {
          startY: infoY,
          head: [
            [
              'Date',
              'Bill No.',
              'Product',
              'Rate',
              'Qty',
              ...(enableDiscount ? ['Discount'] : []),
              'Amt (excl tax)',
              'Amt (incl tax)',
            ],
          ],
          body: tableData,
          theme: 'plain', // changed from 'striped'
          didDrawCell: function (data) {
            if (data.section === 'head') {
              const { cell } = data;
              const { x, y, width, height } = cell;

              // Draw bottom border line for header cells
              doc.setDrawColor(0); // Black border
              doc.setLineWidth(0.1); // Border thickness
              doc.line(x, y + height, x + width, y + height);
            }
          },
          headStyles: {
            fillColor: '#ffffff',
            textColor: [0, 0, 0],
            font: 'Roboto-Bold',
            fontStyle: 'bold',
            halign: 'left',
            valign: 'middle',
            fontSize: 9,
            overflow: 'visible',
            wordBreak: 'normal',
            cellWidth: 'auto',
            cellPadding: { top: 1, right: 1, bottom: 2, left: 1 },
          },
          styles: {
            font: 'Roboto-Regular',
            fontSize: 9,
            halign: 'left',
            valign: 'middle',
            textColor: '#000000',
            wordBreak: 'normal',
            cellPadding: { top: 3, right: 1, bottom: 1, left: 1 },
            cellWidth: 'auto',
          },
          margin: { left: margin, right: margin },
        });

        // Summary Table
        if (enableProductSummary) {
          autoTable(doc, {
            startY: doc.autoTable.previous.finalY + 10,
            head: [['Product', 'Quantity', 'Amt (incl tax)']],
            body: tableData2,
            theme: 'plain',
            didDrawCell: function (data) {
              if (data.section === 'head') {
                const { cell } = data;
                const { x, y, width, height } = cell;

                // Draw bottom border line for header cells
                doc.setDrawColor(0); // Black border
                doc.setLineWidth(0.1); // Border thickness
                doc.line(x, y + height, x + width, y + height);
              }
            },
            headStyles: {
              fillColor: '#ffffff',
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              font: 'Roboto-Bold',
              halign: 'left',
              valign: 'middle',
              fontSize: 9,
              cellPadding: { top: 1, right: 1, bottom: 2, left: 1 },
            },
            styles: {
              font: 'Roboto-Regular',
              fontSize: 9,
              halign: 'left',
              valign: 'middle',
              cellPadding: { top: 3, right: 1, bottom: 1, left: 1 },
              textColor: '#000000',
            },
            margin: { left: margin, right: margin },
            tableWidth: pageWidth - 2 * margin,
          });
        }

        // Billing Totals
        autoTable(doc, {
          body: [
            ...(enableDiscount
              ? [
                  [
                    {
                      content: `Total Discount: ${discount}`,
                      styles: {
                        halign: 'right',
                        font: 'Roboto-Bold',
                        fontSize: 10,
                        fontStyle: 'bold',
                      },
                    },
                  ],
                ]
              : []),
            [
              {
                content: `Billed Amount: ${total}`,
                styles: {
                  halign: 'right',
                  font: 'Roboto-Bold',
                  fontSize: 10,
                  fontStyle: 'bold',
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { left: margin, right: margin },
        });

        // Footer Message
        const footerStartY = doc.autoTable.previous.finalY + 12;
        // doc.setFontSize(10);
        // doc.text('THANK YOU ! FOR FUELLING WITH US !', pageWidth / 2, footerStartY, { align: 'center' });
        // doc.setFontSize(8);
        // doc.text('FOR ANY QUERIES AND COMPLAINT VISIT OUR CUSTOMER CARE', pageWidth / 2, footerStartY + 6, { align: 'center' });
        // doc.text('SAVE FUEL, SECURE THE FUTURE!', pageWidth / 2, footerStartY + 12, { align: 'center' });
        // doc.text(`TIME: ${dayjs(additionalData.invoice_date).format('HH:mm')}`, pageWidth / 2, footerStartY + 18, { align: 'center' });

        // Notes + QR Code (Optional)
        const hasNotes =
          additionalData.notes && additionalData.notes.trim() !== '';
        const hasQR = additionalData.upi_id !== '';

        if (hasNotes || hasQR) {
          autoTable(doc, {
            body: [
              [
                {
                  content: hasNotes ? additionalData.notes : '',
                  styles: {
                    fontSize: 9,
                    cellWidth: pageWidth / 2,
                    halign: 'left',
                  },
                },
                {
                  content: hasQR ? 'Scan the below QR code to pay via UPI' : '',
                  styles: {
                    fontSize: 10,
                    halign: 'right',
                  },
                },
              ],
            ],
            theme: 'plain',
            margin: { left: margin, right: margin },
            styles: {
              overflow: 'linebreak',
            },
          });

          if (hasQR) {
            doc.addImage(
              upiQr,
              'PNG',
              pageWidth - 45,
              doc.autoTable.previous.finalY + 5,
              20,
              20,
            );
            // add a padding at the bottom
            doc.autoTable.previous.finalY += 15;
          }
        }

        // Save PDF
        doc.save(
          `Invoice - ${customer_name} - ${dayjs(additionalData.invoice_date).format('DD-MM-YYYY')}`,
        );
        resolve(true);
      } catch (error) {
        console.error('Error generating invoice:', error);
        reject('There is no data to generate invoice');
      }
    };
    img.src = result;
  });
}

export async function generateLiveMemo(data, resolve, reject) {
  const bunkLogo = getBase64FromUrl(authService.currentOMCWebLogo);

  // read promise value
  bunkLogo.then((result) => {
    // calcualte image width and height based on aspect ratio
    const img = new Image();
    img.onload = function () {
      const doc = new jsPDF({
        orientation: 'landscape',
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.addFileToVFS('Inter-Regular.ttf', InterFont);
      doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
      doc.setFont('Inter');
      const bunkAddress = authService.currentBunkAddress;
      const tableData = [];
      const total = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
      }).format(data.after_tax || 0);

      data.product_list.map((item, i) => {
        tableData.push([
          item.product.name,
          item.price,
          item.quantity.toFixed(2),
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(item.amount.toFixed(2)),
          Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(item.after_tax.toFixed(2)),
        ]);
      });
      const aspectRatio = img.width / img.height;
      const width = 15;
      const height = width / aspectRatio;
      const tableWidth = pageWidth - 33;
      try {
        doc.addImage(result, 'PNG', 13, 10, width, height);
        autoTable(doc, {
          body: [
            [
              {
                content: authService.currentBunkName,
                styles: {
                  halign: 'left',
                  fontSize: 12,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `GST: ${authService.currentUserGst}`,
                styles: {
                  halign: 'left',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content:
                  bunkAddress.city +
                  ', ' +
                  bunkAddress.location +
                  ',' +
                  bunkAddress.country.country +
                  ' - ' +
                  bunkAddress.pincode,
                styles: {
                  halign: 'left',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Date: ${dayjs(data.time_stamp).format('DD-MM-YYYY')} `,
                styles: {
                  halign: 'left',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Invoice no: ${data.bill_number} `,
                styles: {
                  halign: 'left',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
          ],
          theme: 'plain',
          startY: 28,
        });
        autoTable(doc, {
          body: [
            [
              {
                content: `Customer name: ${data.customer_name}`,
                styles: {
                  halign: 'right',
                  fontSize: 10,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `GST: ${data.gst || 'N/A'}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Vehicle no: ${data.vehicle}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Mobile: ${data.mobile}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Email: ${data.email}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
          ],
          theme: 'plain',
          startY: 28,
          margin: { right: pageWidth / 2 + 8.5 },
        });
        autoTable(doc, {
          body: [
            [
              {
                content: `Address: ${data.address}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                  cellWidth: 'wrap',
                  overFlow: 'linebreak',
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { right: pageWidth / 2 + 8 },
          startY: doc.autoTable.previous.finalY - 1,
        });
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        doc.line(
          13,
          doc.autoTable.previous.finalY + 1,
          pageWidth - 13,
          doc.autoTable.previous.finalY + 1,
        );

        autoTable(doc, {
          head: [['Product', 'Rate', 'Quantity', 'Amount', 'Amount(incl tax)']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: '#000000',
          },
          styles: {
            font: 'Inter',
            fontStyle: 'normal',
            fontSize: 8,
          },
          columnStyles: {
            // Adjudsting column widths
            0: { columnWidth: tableWidth * 0.1 },
            1: { columnWidth: tableWidth * 0.1 },
            2: { columnWidth: tableWidth * 0.1 },
            3: { columnWidth: tableWidth * 0.1 },
            4: { columnWidth: tableWidth * 0.1 },
          },
          margin: { left: 10 },
        });
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        doc.line(
          13,
          doc.autoTable.previous.finalY + 5,
          pageWidth - 13,
          doc.autoTable.previous.finalY + 5,
        );
        autoTable(doc, {
          body: [
            [
              {
                content: `Payment mode: ${data.source}`,
                styles: {
                  halign: 'center',
                  fontSize: 7,
                },
              },
            ],
            [
              {
                content: `Total amount: ${total}`,
                styles: {
                  halign: 'center',
                  font: 'Inter',
                  fontStyle: 'normal',
                  fontSize: 7,
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { left: -20 },
        });
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        doc.line(
          13,
          doc.autoTable.previous.finalY + 1,
          pageWidth - 13,
          doc.autoTable.previous.finalY + 1,
        );
        autoTable(doc, {
          body: [
            [
              {
                content: `Notes - ${data.notes}`,
                styles: {
                  halign: 'left',
                  fontSize: 7,
                  textColor: '#000000',
                  cellWidth: 'wrap',
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { left: 13, right: pageWidth / 2 + 10 },
          startY: doc.autoTable.previous.finalY + 10,
        });

        // Save current Y position for bunk copy
        const bunkCopyY = doc.autoTable.previous.finalY + 10;

        // Draw a vertical line to separate customer and bunk copies
        doc.setLineWidth(0.3);
        doc.setDrawColor(0, 0, 0);
        doc.line(pageWidth / 2, 10, pageWidth / 2, bunkCopyY);

        // Draw Bunk copy
        doc.addImage(result, 'PNG', pageWidth / 2 + 10, 10, width, height);
        autoTable(doc, {
          body: [
            [
              {
                content: `Customer name: ${data.customer_name}`,
                styles: {
                  halign: 'right',
                  fontSize: 10,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `GST: ${data.gst || 'N/A'}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Vehicle no: ${data.vehicle}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Mobile: ${data.mobile}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Email: ${data.email}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
          ],
          theme: 'plain',
          startY: 28,
          margin: { left: pageWidth / 2 + 8.5 },
        });
        autoTable(doc, {
          body: [
            [
              {
                content: `Address: ${data.address}`,
                styles: {
                  halign: 'right',
                  fontSize: 8,
                  textColor: '#000000',
                  cellWidth: 'wrap',
                  overFlow: 'linebreak',
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { left: pageWidth / 2 + 8 },
          startY: doc.autoTable.previous.finalY - 1,
        });
        autoTable(doc, {
          startY: bunkCopyY,
          body: [
            [
              {
                content: authService.currentBunkName,
                styles: {
                  fontSize: 12,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `GST: ${authService.currentUserGst}`,
                styles: {
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content:
                  bunkAddress.city +
                  ', ' +
                  bunkAddress.location +
                  ',' +
                  bunkAddress.country.country +
                  ' - ' +
                  bunkAddress.pincode,
                styles: {
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Date: ${dayjs(data.time_stamp).format('DD-MM-YYYY')} `,
                styles: {
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
            [
              {
                content: `Invoice no: ${data.bill_number} `,
                styles: {
                  fontSize: 8,
                  textColor: '#000000',
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { left: 160 },
          // startY: 22,
          startY: 28,
        });

        autoTable(doc, {
          startY: bunkCopyY + 7,
          head: [['Product', 'Rate', 'Quantity', 'Amount', 'Amount(incl tax)']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: '#000000',
          },
          styles: {
            font: 'Inter',
            fontStyle: 'normal',
            fontSize: 8,
          },
          columnStyles: {
            // Adjusting column widths
            0: { columnWidth: tableWidth * 0.1 },
            1: { columnWidth: tableWidth * 0.1 },
            2: { columnWidth: tableWidth * 0.1 },
            3: { columnWidth: tableWidth * 0.1 },
            4: { columnWidth: tableWidth * 0.1 },
          },
          margin: { left: 155 },
          startY: 75,
        });
        autoTable(doc, {
          body: [
            [
              {
                content: `Payment mode: ${data.source}`,
                styles: {
                  halign: 'right',
                  fontSize: 7,
                },
              },
            ],
            [
              {
                content: `Total amount: ${total}`,
                styles: {
                  halign: 'right',
                  font: 'Inter',
                  fontStyle: 'normal',
                  fontSize: 7,
                },
              },
            ],
          ],
          theme: 'plain',
        });
        autoTable(doc, {
          body: [
            [
              {
                content: `Notes - ${data.notes}`,
                styles: {
                  fontSize: 7,
                  textColor: '#000000',
                  cellWidth: 'wrap',
                },
              },
            ],
          ],
          theme: 'plain',
          margin: { left: pageWidth / 2 + 10 },
          startY: doc.autoTable.previous.finalY + 10,
        });

        if (
          doc.save(
            `Invoice - ${data.customer_name} - ${dayjs(data.time_stamp).format('DD-MM-YYYY')}`,
          )
        ) {
          resolve(true);
        } else {
          reject('Error generating memo');
        }
      } catch (error) {
        reject(error);
      }
    };
    img.src = result;
  });
}

export function generateThermalMemo(data) {
  const LINE_WIDTH = 32;

  const safe = (x) => (x ? String(x) : '');

  const padRight = (text, length) => {
    text = safe(text);
    return text.length > length
      ? text.slice(0, length)
      : text.padEnd(length, ' ');
  };

  const padLeft = (text, length) => {
    text = safe(text);
    return text.length > length
      ? text.slice(0, length)
      : text.padStart(length, ' ');
  };

  const centerText = (text) => {
    text = safe(text);
    if (text.length >= LINE_WIDTH) return text.slice(0, LINE_WIDTH);
    const spaces = Math.max(Math.floor((LINE_WIDTH - text.length) / 2), 0);
    return ' '.repeat(spaces) + text;
  };

  // hard wrap (character-based) for thermal reliability
  const wrapText = (text, maxWidth) => {
    text = safe(text);
    const lines = [];
    while (text.length > maxWidth) {
      lines.push(text.slice(0, maxWidth));
      text = text.slice(maxWidth);
    }
    if (text.length > 0) lines.push(text);
    return lines;
  };

  const divider = '-'.repeat(LINE_WIDTH);

  // ---------- Totals ----------
  const items = Array.isArray(data.product_list) ? data.product_list : [];
  const subtotal = items.reduce((sum, it) => sum + Number(it.amount || 0), 0);

  const itemDiscountSum = items.reduce(
    (sum, it) => sum + Number(it.discount || 0),
    0,
  );
  const headerDiscount = Number(data.discount || 0);
  const discount = headerDiscount > 0 ? headerDiscount : itemDiscountSum;

  const preTax = Math.max(0, subtotal - discount);
  const grandTotal = Number(
    data.after_tax != null
      ? data.after_tax
      : data.total != null
        ? data.total
        : preTax,
  );

  const taxTotal = Math.max(0, grandTotal - preTax);

  // detect CGST/SGST presence
  const taxNames = new Set(
    items.flatMap((it) =>
      Array.isArray(it.tax)
        ? it.tax.map((t) => (t?.name || '').toUpperCase())
        : [],
    ),
  );
  const hasCGST = taxNames.has('CGST');
  const hasSGST = taxNames.has('SGST');

  let output = '';

  // ---------- Header (use your auth/global details if available) ----------
  const companyName = safe(authService?.currentBunkName) || 'YOUR COMPANY';
  const addrLines = safe(authService?.currentBunkAddress) || ''; // you can also combine your structured address if needed
  const phone = safe(authService?.currentPhone);
  const gstin = safe(authService?.currentUserGst);

  wrapText(companyName, LINE_WIDTH).forEach(
    (l) => (output += centerText(l) + '\n'),
  );
  if (addrLines)
    wrapText(addrLines, LINE_WIDTH).forEach(
      (l) => (output += centerText(l) + '\n'),
    );
  if (phone) output += centerText(`PHONE : ${phone}`) + '\n';
  if (gstin) output += centerText(`GSTIN : ${gstin}`) + '\n';

  output += '\n' + centerText('Retail Invoice') + '\n\n';

  // ---------- Meta ----------
  output += `Date : ${dayjs(data.time_stamp).format('DD/MM/YYYY')}, ${dayjs(data.time_stamp).format('hh:mm A')}\n\n`;

  wrapText(data.customer_name, LINE_WIDTH).forEach((l) => (output += l + '\n'));
  output += `Bill No : ${safe(data.bill_number)}\n`;
  if (data.vehicle) output += `Vehicle : ${safe(data.vehicle)}\n`;
  if (data.mobile) output += `Mobile  : ${safe(data.mobile)}\n`;
  if (data.email) output += `Email   : ${safe(data.email)}\n`;
  output += `Payment Mode : ${safe(data.source)}\n\n`;

  if (data.address) {
    wrapText(`Address: ${data.address}`, LINE_WIDTH).forEach(
      (l) => (output += l + '\n'),
    );
  }

  // ---------- Items ----------
  output += divider + '\n';
  output += `${padRight('Item', 20)}${padLeft('Qty', 4)}${padLeft('Amt', 8)}\n`;
  output += divider + '\n';

  items.forEach((it) => {
    const nameLines = wrapText(it.product?.name || '', 20);
    const qty = safe(Number(it.quantity || 0).toString());
    const amt = safe(Number(it.amount || 0).toFixed(2));

    // first line with columns
    output +=
      padRight(nameLines[0], 20) + padLeft(qty, 4) + padLeft(amt, 8) + '\n';
    // wrapped name continuation lines
    for (let i = 1; i < nameLines.length; i++) {
      output += padRight(nameLines[i], 20) + '\n';
    }
  });

  output += divider + '\n';

  // ---------- Summary ----------
  output += `${padRight('Sub Total', 24)}${padLeft(subtotal.toFixed(2), 8)}\n`;

  if (discount > 0) {
    output += `${padRight('(-) Discount', 24)}${padLeft(discount.toFixed(2), 8)}\n`;
  }

  if (taxTotal > 0) {
    if (hasCGST && hasSGST) {
      const half = (taxTotal / 2).toFixed(2);
      output += `${padRight('CGST', 24)}${padLeft(half, 8)}\n`;
      output += `${padRight('SGST', 24)}${padLeft(half, 8)}\n`;
    } else {
      output += `${padRight('Tax', 24)}${padLeft(taxTotal.toFixed(2), 8)}\n`;
    }
  }

  output += '\n';
  output += `${padRight('TOTAL', 24)}${padLeft(grandTotal.toFixed(2), 8)}\n`;
  output += divider + '\n\n';

  // ---------- Payment Breakdown ----------
  // output += `${padRight("Cash :", 24)}${padLeft(grandTotal.toFixed(2), 8)}\n`;
  // output += `${padRight("Cash tendered :", 24)}${padLeft(grandTotal.toFixed(2), 8)}\n\n`;

  // ---------- Footer ----------
  // output += centerText("E & O E") + "\n";

  return output;
}
